import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { produce } from 'immer'
import isNumber from 'lodash/isNumber'
import toInteger from 'lodash/toInteger'
import toString from 'lodash/toString'
import { Plus } from 'lucide-react'
import type { PayablePaymentInput } from '@klear/payable/payable.wire'
import { paymentFromPayable } from '@klear/payable/payment-from-payable'
import { payableTotalFromComponents } from '@klear/payable/total'
import type { Payment } from '@klear/payment/payment'
import { Money } from '@klear/money'
import { ROUTE_EXTERNAL_BANK, ROUTE_SUPPLIERS, buildNavPathTrader } from '@klear/constants'
import { isEventLine } from '@klear/payment/line-item'
import { trpc } from '@klear/ui/lib/trpc'
import { useSupplierExternalBankAccounts } from '@klear/ui/hooks/use-supplier-external-bank-accounts'
import { useKlearFinancialAccount } from '@klear/ui/hooks/use-klear-financial-account'
import { useOperationals } from '@klear/ui/hooks/use-operationals'
import { Card } from '@klear/ui/components/card'
import { Button } from '@klear/ui/components/button'
import { CreateEntityReturnLink } from '@/app/workflow/create-entity-return-link'

import {
  PaymentStepFields,
  clearPaymentStepFormErrors,
  hasPaymentStepFormErrors,
  paymentStepFormDefaults,
  paymentStepFormFromPayment,
  paymentStepFormFromInput,
  paymentInputFromStepForm,
  reconcilePaymentStepFormAccounts,
  validatePaymentStepForm,
  type PaymentStepForm,
  type PaymentStepFormErrorMap
} from '@/app/payable/workflow/payment-step-fields'
import type { PaymentReceipt } from '@/app/payable/workflow/payment-step-fields'
import { filenameForContentType } from '@klear/upload/content-type'
import type { WizardStepComponentProps } from '@klear/ui/wizard/flow'
import type { PayableStepCtx } from '@/app/payable/workflow/PayableWizardContext'
import { uploadDocument } from '@klear/ui/lib/upload-document'
import { receiptDownloadUrl } from '@/app/payable/receipt-url'

type PaymentStepFormPanelProps = {
  formKey: string
  storedPaymentData: PayablePaymentInput | null
  seedPayment: Payment | null
  defaultValues: PaymentStepForm
  derivedAmount: Money
  linkedEventCount: number
  itemCount: number
  externalBankAccounts: ReturnType<typeof useSupplierExternalBankAccounts>['supplierExternalBankAccounts']
  klearFinancialAccount: ReturnType<typeof useKlearFinancialAccount>['klearFinancialAccount']
  klearFinancialAccounts: ReturnType<typeof useKlearFinancialAccount>['accounts']
  destinationLocked: boolean
  supplierId?: number
  traderIdParam: string
  receipts: PaymentReceipt[]
  onUploadReceipts?: (files: File[]) => Promise<void>
  onRemoveReceipt?: (documentId: number) => Promise<void>
  pendingReceipts: File[]
  onAddPendingReceipts?: (files: File[]) => void
  onRemovePendingReceipt: (index: number) => void
  getReceiptDownloadUrl?: (documentId: number) => string | null
  onSave: (payment: PayablePaymentInput) => void
}

function PaymentStepFormPanel({
  storedPaymentData,
  seedPayment,
  defaultValues,
  derivedAmount,
  linkedEventCount,
  itemCount,
  externalBankAccounts,
  klearFinancialAccount,
  klearFinancialAccounts,
  destinationLocked,
  supplierId,
  traderIdParam,
  receipts,
  onUploadReceipts,
  onRemoveReceipt,
  pendingReceipts,
  onAddPendingReceipts,
  onRemovePendingReceipt,
  getReceiptDownloadUrl,
  onSave
}: PaymentStepFormPanelProps) {
  const [paymentValues, setPaymentValues] = useState<PaymentStepForm>(() => {
    const base =
      storedPaymentData != null
        ? paymentStepFormFromInput(storedPaymentData, defaultValues)
        : seedPayment != null
          ? paymentStepFormFromPayment(seedPayment, defaultValues)
          : defaultValues
    const reconciled = reconcilePaymentStepFormAccounts(base, defaultValues, externalBankAccounts)
    return { ...reconciled, amount: derivedAmount }
  })
  const [formErrors, setFormErrors] = useState<PaymentStepFormErrorMap>({})

  useEffect(() => {
    setPaymentValues((current) => {
      const unchanged =
        current.amount.amount === derivedAmount.amount && current.amount.currency === derivedAmount.currency
      return unchanged ? current : { ...current, amount: derivedAmount }
    })
  }, [derivedAmount])

  const handleFieldChange = useCallback(<K extends keyof PaymentStepForm>(field: K, value: PaymentStepForm[K]) => {
    setPaymentValues((current) =>
      produce(current, (draft) => {
        draft[field] = value as never
      })
    )
    setFormErrors((current) => clearPaymentStepFormErrors(current, [field]))
  }, [])

  const handleFormUpdate = useCallback((recipe: (draft: PaymentStepForm) => void) => {
    setPaymentValues((current) => produce(current, recipe))
    setFormErrors((current) => {
      const next = { ...current }
      delete next.sourceAccountId
      delete next.destinationAccountId
      delete next.paymentNetwork
      return next
    })
  }, [])

  const handleSave = useCallback(() => {
    const validationErrors = validatePaymentStepForm(paymentValues)
    if (hasPaymentStepFormErrors(validationErrors)) {
      setFormErrors(validationErrors)
      return
    }
    onSave(paymentInputFromStepForm(paymentValues))
  }, [onSave, paymentValues])

  const rootError = formErrors.root
  const createBankAccountTo =
    supplierId != null && supplierId > 0
      ? buildNavPathTrader(traderIdParam, ROUTE_SUPPLIERS, toString(supplierId), ROUTE_EXTERNAL_BANK, 'new')
      : null

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        handleSave()
      }}
    >
      <div className="space-y-4">
        {rootError ? <p className="text-sm text-destructive">{rootError}</p> : null}
        <PaymentStepFields
          values={paymentValues}
          errors={formErrors}
          linkedEventCount={linkedEventCount}
          itemCount={itemCount}
          externalBankAccounts={externalBankAccounts}
          klearFinancialAccount={klearFinancialAccount}
          klearFinancialAccounts={klearFinancialAccounts}
          destinationLocked={destinationLocked}
          onFieldChange={handleFieldChange}
          onFormUpdate={handleFormUpdate}
          receipts={receipts}
          onUploadReceipts={onUploadReceipts}
          onRemoveReceipt={onRemoveReceipt}
          pendingReceipts={pendingReceipts}
          onAddPendingReceipts={onAddPendingReceipts}
          onRemovePendingReceipt={onRemovePendingReceipt}
          getReceiptDownloadUrl={getReceiptDownloadUrl}
          createBankAccountAction={
            createBankAccountTo != null ? (
              <CreateEntityReturnLink to={createBankAccountTo}>
                <Plus className="h-3 w-3 mr-1" />
                Create bank account
              </CreateEntityReturnLink>
            ) : undefined
          }
        />
        <div className="flex justify-end">
          <Button type="submit">Next</Button>
        </div>
      </div>
    </form>
  )
}

export default function PaymentStep({ ctx }: WizardStepComponentProps<PayableStepCtx>) {
  const { form, dirty, patchFormAndGoNext, isLoadingPayable, previewResult, payable } = ctx
  const { pendingReceipts, addPendingReceipts, removePendingReceipt } = ctx
  const { traderId: traderIdParam } = useParams()
  const traderId = toInteger(traderIdParam)
  const anchorOperationalEventId = form.operationalEventIds[0] ?? 0
  const payableId = form.payableId ?? 0

  const deferReceipts = payableId < 1 || pendingReceipts.length > 0
  const utils = trpc.useUtils()
  const { data: receiptList } = trpc.documents.list.useQuery(
    { paymentIds: [payableId], traderId, limit: 100 },
    { enabled: payableId > 0 && traderId > 0 }
  )
  const receipts = useMemo(
    () =>
      (receiptList?.rows ?? []).map(({ document }) => ({
        id: document.id,
        name: filenameForContentType(document.name, document.contentType)
      })),
    [receiptList]
  )
  const removeFromPayment = trpc.documents.update.useMutation()

  const onUploadReceipts = useCallback(
    async (files: File[]): Promise<void> => {
      if (payableId < 1) return
      for (const file of files) await uploadDocument(traderId, file, { fileOn: { kind: 'payment', id: payableId } })
      await utils.documents.list.invalidate()
    },
    [traderId, payableId, utils]
  )

  const onRemoveReceipt = useCallback(
    async (documentId: number): Promise<void> => {
      await removeFromPayment.mutateAsync({
        id: documentId,
        data: { removeFrom: [{ kind: 'payment', id: payableId }] }
      })
      await utils.documents.list.invalidate()
    },
    [removeFromPayment, payableId, utils]
  )

  const getReceiptDownloadUrl = useCallback(
    (documentId: number) => receiptDownloadUrl(traderId, documentId),
    [traderId]
  )

  const { data: operationalEvent } = trpc.operational.get.useQuery(
    { id: anchorOperationalEventId, traderId },
    { enabled: anchorOperationalEventId > 0 }
  )

  const componentsDirty = dirty.items === true || dirty.operationalEventIds === true
  const serverPayable = previewResult ?? (componentsDirty ? null : payable)

  const supplierId = operationalEvent?.supplierId ?? serverPayable?.supplierId ?? form.supplierId

  const supplierIdForAccounts =
    supplierId == null ? undefined : isNumber(supplierId) ? supplierId : toInteger(supplierId)

  const { supplierExternalBankAccounts: externalBankAccounts } = useSupplierExternalBankAccounts(supplierIdForAccounts)
  const { accounts: klearFinancialAccounts, openAccounts, receivableAccount } = useKlearFinancialAccount()
  const klearFinancialAccount = (receivableAccount?.isOpen ? receivableAccount : null) ?? openAccounts[0] ?? null

  const parentAmount = useMemo((): Money | undefined => {
    if (operationalEvent?.amount != null) {
      return new Money(operationalEvent.amount)
    }
    return undefined
  }, [operationalEvent?.amount])

  const currency = serverPayable?.amount.currency ?? parentAmount?.currency ?? form.currency

  const defaultValues = useMemo(
    () =>
      paymentStepFormDefaults({
        parentAmount,
        parentCurrency: currency,
        externalBankAccounts,
        klearFinancialAccount
      }),
    [externalBankAccounts, klearFinancialAccount, parentAmount, currency]
  )

  const previewPayment = useMemo(
    () => (previewResult != null ? paymentFromPayable(previewResult) : null),
    [previewResult]
  )

  const storedPaymentData = form.payment

  const destinationLocked = previewPayment?.stripePaymentId != null

  const serverEventLines = serverPayable?.lineItems.filter(isEventLine)
  const linkedEventCount = serverEventLines?.length ?? form.operationalEventIds.length
  const itemCount =
    serverPayable != null ? serverPayable.lineItems.length - (serverEventLines?.length ?? 0) : form.items.length

  const { operationals: pickedOperationals } = useOperationals(
    { orderEventId: form.operationalEventIds },
    { enabled: serverPayable == null && form.operationalEventIds.length > 0 }
  )

  const derivedAmount = useMemo(() => {
    if (serverPayable?.amount != null) return serverPayable.amount
    const components = [
      ...pickedOperationals.map((operational) => operational.amount),
      ...form.items.map((item) => item.amount)
    ].map((amount) => new Money(amount))
    if (components.length === 0) return defaultValues.amount
    return payableTotalFromComponents(components, components[0]!.currency).total
  }, [serverPayable?.amount, pickedOperationals, form.items, defaultValues.amount])

  const formKey = useMemo(
    () =>
      [
        payableId,
        storedPaymentData ? JSON.stringify(storedPaymentData) : '',
        defaultValues.sourceAccountId,
        defaultValues.destinationAccountId
      ].join(':'),
    [defaultValues.destinationAccountId, defaultValues.sourceAccountId, payableId, storedPaymentData]
  )

  const handleSave = useCallback(
    (payment: PayablePaymentInput) => {
      patchFormAndGoNext((draft) => {
        draft.payment = payment
      }, 'payment')
    },
    [patchFormAndGoNext]
  )

  const hasComponents = form.operationalEventIds.length > 0 || form.items.length > 0

  if (!hasComponents) {
    return <div className="text-sm text-muted-foreground">Add something for this payment to cover first.</div>
  }

  if (payableId > 0 && isLoadingPayable && storedPaymentData == null && previewPayment == null) {
    return <div className="text-sm text-muted-foreground">Loading payment…</div>
  }

  return (
    <div className="w-full max-w-2xl">
      <Card className="p-6 space-y-4">
        <h2 className="text-lg font-semibold text-foreground">{payableId ? 'Edit payment' : 'Create payment'}</h2>
        <p className="text-sm text-muted-foreground">
          Source, destination, network, amount and effective date for the payable.
        </p>
        <PaymentStepFormPanel
          key={formKey}
          formKey={formKey}
          storedPaymentData={storedPaymentData}
          seedPayment={storedPaymentData == null ? previewPayment : null}
          defaultValues={defaultValues}
          derivedAmount={derivedAmount}
          linkedEventCount={linkedEventCount}
          itemCount={itemCount}
          externalBankAccounts={externalBankAccounts}
          klearFinancialAccount={klearFinancialAccount}
          klearFinancialAccounts={klearFinancialAccounts}
          destinationLocked={destinationLocked}
          supplierId={supplierIdForAccounts}
          traderIdParam={traderIdParam ?? toString(traderId)}
          receipts={receipts}
          onUploadReceipts={onUploadReceipts}
          onRemoveReceipt={onRemoveReceipt}
          pendingReceipts={pendingReceipts}
          onAddPendingReceipts={deferReceipts ? addPendingReceipts : undefined}
          onRemovePendingReceipt={removePendingReceipt}
          getReceiptDownloadUrl={getReceiptDownloadUrl}
          onSave={handleSave}
        />
      </Card>
    </div>
  )
}
